/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


// Hide the wrong newsletter success banner based on the hash after a customer form submission.
document.addEventListener("DOMContentLoaded", () => {
  const params = new URLSearchParams(window.location.search);
  if (!params.has("customer_posted") && !params.has("contact_posted")) return;

  const hash = window.location.hash || "";
  const popup = document.getElementById("newsletter-popup");
  const popupBanner = popup ? popup.querySelector(".newsletter-modal__form .form__banner") : null;
  const footerBanner = document.querySelector("#footer-newsletter .form__banner");

  if (hash.startsWith("#footer-newsletter")) {
    if (popupBanner) popupBanner.style.display = "none";
    if (popup) popup.open = false;
    return;
  }

  if (hash.startsWith("#newsletter-popup")) {
    if (footerBanner) footerBanner.style.display = "none";
    if (popup) popup.open = true;
  }
});

(function() {
  if (window.themeVariables.settings.pageType !== "collection") return;

  const DESKTOP_PRODUCTS_PER_GROUP = 40;
  const MOBILE_PRODUCTS_PER_GROUP = 24;
  const MOBILE_QUERY = "(max-width: 740px)";
  const NEXT_GROUP_DELAY = 300;
  const mobileMedia = window.matchMedia(MOBILE_QUERY);
  const docElement = document.documentElement;
  let observer = null;
  let sentinel = null;
  let isLoading = false;
  let currentListInner = null;
  let rearmOnScroll = false;
  let visibleProductsLimit = getProductsPerGroup();

  function getProductsPerGroup() {
    return mobileMedia.matches ? MOBILE_PRODUCTS_PER_GROUP : DESKTOP_PRODUCTS_PER_GROUP;
  }

  const isInfiniteRevealEnabled = () => document.querySelector("[data-collection-infinite-reveal-css]") !== null;
  const isOnCollectionPage = () => document.querySelector(".shopify-section--main-collection") !== null;
  const getFacetSection = () => document.querySelector("product-facet[section-id]");
  const getSectionId = () => {
    const facet = getFacetSection();
    return facet ? facet.getAttribute("section-id") : null;
  };
  const getProductListInner = () => document.querySelector("#facet-main .product-list__inner");
  const getPagination = () => document.querySelector("#facet-main .pagination");
  const getProgress = () => document.querySelector("#facet-main [data-infinite-scroll-progress]");
  const getNextPageUrl = () => {
    const pagination = getPagination();
    if (!pagination) return null;
    const nextLink = pagination.querySelector('a[rel="next"]');
    return nextLink ? nextLink.getAttribute("href") : null;
  };
  const getLoadedProductsCount = () => {
    const listInner = getProductListInner();
    return listInner ? listInner.querySelectorAll(".product-item:not([hidden])").length : 0;
  };
  const getAllLoadedProductItems = () => {
    const listInner = getProductListInner();
    return listInner ? Array.from(listInner.querySelectorAll(".product-item")) : [];
  };

  const syncListState = () => {
    const listInner = getProductListInner();

    if (listInner !== currentListInner) {
      currentListInner = listInner;
      visibleProductsLimit = getProductsPerGroup();
    }

    return listInner;
  };

  const updateProgress = () => {
    const progress = getProgress();
    if (!progress) return;

    const loadedProductsCount = getLoadedProductsCount();
    const totalProductsCount = Number.parseInt(progress.getAttribute("data-total"), 10) || loadedProductsCount;
    const countElement = progress.querySelector("[data-infinite-scroll-count]");
    const totalElement = progress.querySelector("[data-infinite-scroll-total]");
    const progressRatio = totalProductsCount > 0 ? Math.min(loadedProductsCount / totalProductsCount * 100, 100) : 0;

    if (countElement) {
      countElement.textContent = loadedProductsCount;
    }

    if (totalElement) {
      totalElement.textContent = totalProductsCount;
    }

    progress.style.setProperty("--collection-load-progress", `${progressRatio}%`);
    progress.classList.toggle("is-loading", isLoading);
    progress.classList.toggle("is-complete", loadedProductsCount >= totalProductsCount);
    progress.hidden = totalProductsCount === 0;
  };

  function handleObserverRearm() {
    rearmOnScroll = false;

    if (hasMoreProducts()) {
      observeSentinel();
    }
  }

  const detachObserver = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    if (rearmOnScroll) {
      window.removeEventListener("scroll", handleObserverRearm);
      rearmOnScroll = false;
    }
  };

  const removeSentinel = () => {
    if (sentinel && sentinel.parentNode) {
      sentinel.parentNode.removeChild(sentinel);
    }
    sentinel = null;
  };

  const resetState = () => {
    detachObserver();
    removeSentinel();
    isLoading = false;
    visibleProductsLimit = getProductsPerGroup();
    const pagination = getPagination();
    if (pagination) {
      pagination.classList.remove("pagination--infinite-scroll");
    }
    updateProgress();
  };

  const ensureSentinel = () => {
    const listInner = syncListState();
    const progress = getProgress();
    if (!listInner || !progress || !progress.parentNode) return null;

    if (!sentinel) {
      sentinel = document.createElement("div");
      sentinel.className = "collection-infinite-scroll-sentinel";
      sentinel.setAttribute("aria-hidden", "true");
    }

    if (sentinel.previousElementSibling !== progress) {
      progress.insertAdjacentElement("afterend", sentinel);
    }

    return sentinel;
  };

  const moveSoldOutProductsToEnd = () => {
    const listInner = syncListState();
    if (!listInner) return;

    const beforeNode = sentinel && sentinel.parentNode === listInner ? sentinel : null;
    Array.from(listInner.children).forEach((item) => {
      if (item.matches && item.matches(".product-item--sold-out")) {
        listInner.insertBefore(item, beforeNode);
      }
    });
  };

  const updateProductVisibility = () => {
    getAllLoadedProductItems().forEach((item, index) => {
      item.toggleAttribute("hidden", index >= visibleProductsLimit);
    });
  };

  const hasHiddenLoadedProducts = () => {
    return getAllLoadedProductItems().some((item) => item.hidden);
  };

  const hasMoreProducts = () => {
    return hasHiddenLoadedProducts() || Boolean(getNextPageUrl());
  };

  const revealNextProducts = () => {
    visibleProductsLimit += getProductsPerGroup();
    updateProductVisibility();
    updateProgress();
  };

  const wait = (duration) => new Promise((resolve) => {
    window.setTimeout(resolve, duration);
  });

  const revealNextProductsWithDelay = async () => {
    isLoading = true;

    if (sentinel) {
      sentinel.classList.add("is-loading");
    }

    updateProgress();

    try {
      await wait(NEXT_GROUP_DELAY);
      revealNextProducts();

      if (!hasMoreProducts()) {
        detachObserver();
        removeSentinel();
      } else {
        scheduleObserverRearm();
      }
    } finally {
      isLoading = false;

      if (sentinel) {
        sentinel.classList.remove("is-loading");
      }

      updateProgress();
    }
  };

  const observeSentinel = () => {
    detachObserver();

    if (!ensureSentinel()) return;

    observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "200px 0px",
      threshold: 0
    });
    observer.observe(sentinel);
  };

  const scheduleObserverRearm = () => {
    detachObserver();
    rearmOnScroll = true;
    window.addEventListener("scroll", handleObserverRearm, { once: true, passive: true });
  };

  const handleIntersection = (entries) => {
    const entry = entries[0];
    if (!entry || !entry.isIntersecting || isLoading) return;

    if (hasHiddenLoadedProducts()) {
      revealNextProductsWithDelay();
      return;
    }

    loadNextPage();
  };

  const loadNextPage = async () => {
    const nextUrl = getNextPageUrl();
    const sectionId = getSectionId();
    const listInner = syncListState();
    const pagination = getPagination();

    if (!nextUrl || !sectionId || !listInner || !pagination || !sentinel) {
      detachObserver();
      removeSentinel();
      updateProgress();
      return;
    }

    isLoading = true;
    sentinel.classList.add("is-loading");
    updateProgress();
    docElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set("section_id", sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`Infinite scroll request failed with status ${response.status}`);
      }

      const html = await response.text();
      const temp = document.createElement("div");
      temp.innerHTML = html;

      const newListInner = temp.querySelector(".product-list__inner");
      const newPagination = temp.querySelector(".pagination");
      const newProgress = temp.querySelector("[data-infinite-scroll-progress]");

      if (newListInner) {
        const fragment = document.createDocumentFragment();
        Array.from(newListInner.children).forEach((item) => fragment.appendChild(item));

        if (sentinel.parentNode === listInner) {
          listInner.removeChild(sentinel);
        }

        listInner.appendChild(fragment);
      }

      if (newProgress) {
        const progress = getProgress();
        if (progress) {
          progress.setAttribute("data-total", newProgress.getAttribute("data-total"));
        }
      }

      if (newPagination) {
        pagination.innerHTML = newPagination.innerHTML;
      } else {
        pagination.remove();
      }

      moveSoldOutProductsToEnd();
      revealNextProducts();

      if (hasMoreProducts()) {
        scheduleObserverRearm();
      } else {
        detachObserver();
        removeSentinel();
      }
    } catch (error) {
      console.error("[InfiniteScroll]", error);
      detachObserver();

      if (sentinel) {
        sentinel.classList.add("is-error");
      }
    } finally {
      isLoading = false;
      if (sentinel) {
        sentinel.classList.remove("is-loading");
      }
      updateProgress();
      docElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
  };

  const initInfiniteScroll = () => {
    if (!isOnCollectionPage() || !isInfiniteRevealEnabled()) {
      resetState();
      return;
    }

    const listInner = syncListState();
    const pagination = getPagination();

    detachObserver();
    moveSoldOutProductsToEnd();
    updateProductVisibility();
    isLoading = false;
    updateProgress();

    if (!listInner || !hasMoreProducts()) {
      removeSentinel();
      return;
    }

    ensureSentinel();
    if (pagination) {
      pagination.classList.add("pagination--infinite-scroll");
    }
    observeSentinel();
  };

  document.addEventListener("DOMContentLoaded", initInfiniteScroll);
  docElement.addEventListener("theme:loading:end", () => {
    window.requestAnimationFrame(initInfiniteScroll);
  });

  if (typeof mobileMedia.addEventListener === "function") {
    mobileMedia.addEventListener("change", initInfiniteScroll);
  } else if (typeof mobileMedia.addListener === "function") {
    mobileMedia.addListener(initInfiniteScroll);
  }
})();

(function() {
  if (window.themeVariables.settings.pageType !== "collection") return;

  const TILE_SELECTOR = ".collection-grid-tile[data-collection-tile-video-mode]";
  const TEMPLATE_SELECTOR = "template[data-collection-grid-tile-video-template]";
  const VIDEO_SELECTOR = ".collection-grid-tile__video";
  const reducedMotionMedia = window.matchMedia("(prefers-reduced-motion: reduce)");
  const observedTiles = new Set();
  const visibleTiles = new Set();
  let resizeFrame = null;

  const isTileVideoEnabled = (tile) => {
    const mode = tile.dataset.collectionTileVideoMode;
    const breakpoint = Number.parseInt(tile.dataset.collectionTileDesktopBreakpoint, 10);

    if (reducedMotionMedia.matches) return false;
    if (mode === "mobile") return window.matchMedia("(max-width: 1199px)").matches;

    return mode === "desktop"
      && Number.isFinite(breakpoint)
      && window.matchMedia(`(min-width: ${breakpoint}px)`).matches;
  };

  const getVideo = (tile) => tile.querySelector(VIDEO_SELECTOR);

  const stopVideo = (tile, reset = false) => {
    tile.classList.remove("is-video-playing");
    const video = getVideo(tile);
    if (!video) return;

    video.pause();

    if (reset && video.readyState > 0) {
      try {
        video.currentTime = 0;
      } catch (error) {
        // The metadata may not be available yet.
      }
    }
  };

  const unloadVideo = (tile) => {
    stopVideo(tile, true);
    const video = getVideo(tile);
    if (video) video.remove();
  };

  const ensureVideo = (tile) => {
    const existingVideo = getVideo(tile);
    if (existingVideo) return existingVideo;

    const template = tile.querySelector(TEMPLATE_SELECTOR);
    if (!template) return null;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector("video");
    if (!video) return null;

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;

    video.addEventListener("playing", () => {
      if (isTileVideoEnabled(tile) && visibleTiles.has(tile) && !document.hidden) {
        tile.classList.add("is-video-playing");
      } else {
        video.pause();
      }
    });
    video.addEventListener("error", () => {
      tile.classList.remove("is-video-playing");
    });

    template.before(fragment);
    video.load();

    return video;
  };

  const playVideo = (tile) => {
    if (!isTileVideoEnabled(tile) || !visibleTiles.has(tile) || document.hidden) return;

    const video = ensureVideo(tile);
    if (!video) return;

    const playPromise = video.play();
    if (playPromise) {
      playPromise.catch(() => {
        tile.classList.remove("is-video-playing");
      });
    }
  };

  const syncTile = (tile) => {
    if (!tile.isConnected) {
      observedTiles.delete(tile);
      visibleTiles.delete(tile);
      return;
    }

    if (!isTileVideoEnabled(tile)) {
      unloadVideo(tile);
      return;
    }

    if (visibleTiles.has(tile) && !document.hidden) {
      playVideo(tile);
    } else {
      stopVideo(tile, true);
    }
  };

  const syncTiles = () => {
    observedTiles.forEach(syncTile);
  };

  const intersectionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        visibleTiles.add(entry.target);
      } else {
        visibleTiles.delete(entry.target);
      }

      syncTile(entry.target);
    });
  }, { threshold: 0.01 });

  const observeTiles = (root) => {
    const tiles = [];

    if (root.nodeType === Node.ELEMENT_NODE && root.matches(TILE_SELECTOR)) {
      tiles.push(root);
    }

    if (typeof root.querySelectorAll === "function") {
      tiles.push(...root.querySelectorAll(TILE_SELECTOR));
    }

    tiles.forEach((tile) => {
      if (observedTiles.has(tile) || !tile.querySelector(TEMPLATE_SELECTOR)) return;

      observedTiles.add(tile);
      intersectionObserver.observe(tile);
    });
  };

  const forgetTiles = (root) => {
    const tiles = [];

    if (root.nodeType === Node.ELEMENT_NODE && root.matches(TILE_SELECTOR)) {
      tiles.push(root);
    }

    if (typeof root.querySelectorAll === "function") {
      tiles.push(...root.querySelectorAll(TILE_SELECTOR));
    }

    tiles.forEach((tile) => {
      stopVideo(tile, true);
      intersectionObserver.unobserve(tile);
      observedTiles.delete(tile);
      visibleTiles.delete(tile);
    });
  };

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach(observeTiles);
      mutation.removedNodes.forEach(forgetTiles);
    });
  });

  observeTiles(document);
  mutationObserver.observe(document.body, { childList: true, subtree: true });

  window.addEventListener("resize", () => {
    if (resizeFrame !== null) return;

    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = null;
      syncTiles();
    });
  });
  document.addEventListener("visibilitychange", syncTiles);
  document.addEventListener("theme:loading:start", () => {
    observedTiles.forEach((tile) => stopVideo(tile));
  });
  document.addEventListener("theme:loading:end", () => {
    observeTiles(document);
    syncTiles();
  });

  if (typeof reducedMotionMedia.addEventListener === "function") {
    reducedMotionMedia.addEventListener("change", syncTiles);
  } else if (typeof reducedMotionMedia.addListener === "function") {
    reducedMotionMedia.addListener(syncTiles);
  }
})();

(function() {
  if (window.themeVariables.settings.pageType !== "collection") return;

  const HOVER_DELAY = 100;
  const HOVER_VIDEO_QUERY = "(min-width: 1000px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)";
  const HOVER_VIDEO_SELECTOR = ".product-item__image-wrapper--hover-video";
  const AUTOPLAY_VIDEO_SELECTOR = ".product-item__image-wrapper--autoplay-video";
  const hoverVideoMedia = window.matchMedia(HOVER_VIDEO_QUERY);
  let hoverTimer = null;
  let pendingWrapper = null;
  let activeWrapper = null;
  let autoplayObserver = null;
  const visibleAutoplayWrappers = new Set();

  const clearPendingHover = () => {
    if (hoverTimer !== null) {
      window.clearTimeout(hoverTimer);
    }

    hoverTimer = null;
    pendingWrapper = null;
  };

  const getHoverVideo = (wrapper) => wrapper.querySelector(".product-item__hover-video");
  const isAutoplayWrapper = (wrapper) => wrapper.matches(AUTOPLAY_VIDEO_SELECTOR);

  const isPlaybackAllowed = (wrapper) => {
    if (!hoverVideoMedia.matches || document.hidden || !wrapper.isConnected) return false;

    if (isAutoplayWrapper(wrapper)) {
      return visibleAutoplayWrappers.has(wrapper) && !wrapper.classList.contains("is-autoplay-video-image-hover");
    }

    return activeWrapper === wrapper && wrapper.matches(":hover");
  };

  const ensureHoverVideo = (wrapper) => {
    const existingVideo = getHoverVideo(wrapper);
    if (existingVideo) return existingVideo;

    const template = wrapper.querySelector("template[data-product-hover-video-template]");
    if (!template) return null;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector("video");
    if (!video) return null;

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;

    video.addEventListener("playing", () => {
      if (isPlaybackAllowed(wrapper)) {
        wrapper.classList.add("is-hover-video-playing");
        return;
      }

      video.pause();
    });

    template.replaceWith(fragment);
    video.load();

    return video;
  };

  const stopHoverVideo = (wrapper) => {
    if (!wrapper) return;

    wrapper.classList.remove("is-hover-video-playing");
    wrapper.classList.remove("is-autoplay-video-active");
    wrapper.classList.remove("is-autoplay-video-image-hover");
    const video = getHoverVideo(wrapper);

    if (video) {
      video.pause();

      if (video.readyState > 0) {
        try {
          video.currentTime = 0;
        } catch (error) {
          // The video metadata may not be available yet.
        }
      }
    }

    if (activeWrapper === wrapper) {
      activeWrapper = null;
    }
  };

  const playHoverVideo = (wrapper) => {
    if (!isPlaybackAllowed(wrapper)) return;

    const video = ensureHoverVideo(wrapper);
    if (!video) return;

    if (isAutoplayWrapper(wrapper)) {
      wrapper.classList.add("is-autoplay-video-active");
    }

    const playPromise = video.play();

    if (playPromise) {
      playPromise.catch(() => {
        stopHoverVideo(wrapper);
      });
    }
  };

  const startHoverVideo = (wrapper) => {
    if (!hoverVideoMedia.matches || !wrapper.isConnected || !wrapper.matches(":hover") || isAutoplayWrapper(wrapper)) return;

    if (activeWrapper && activeWrapper !== wrapper) {
      stopHoverVideo(activeWrapper);
    }

    activeWrapper = wrapper;
    playHoverVideo(wrapper);
  };

  const showAutoplayImage = (wrapper) => {
    wrapper.classList.add("is-autoplay-video-image-hover");
    wrapper.classList.remove("is-autoplay-video-active");
    wrapper.classList.remove("is-hover-video-playing");

    const video = getHoverVideo(wrapper);
    if (video) video.pause();
  };

  const hideAutoplayImage = (wrapper) => {
    wrapper.classList.remove("is-autoplay-video-image-hover");
    playHoverVideo(wrapper);
  };

  const handlePointerOver = (event) => {
    if (!hoverVideoMedia.matches) return;

    const wrapper = event.target.closest(HOVER_VIDEO_SELECTOR);
    if (!wrapper || wrapper.contains(event.relatedTarget)) return;

    if (isAutoplayWrapper(wrapper)) {
      showAutoplayImage(wrapper);
      return;
    }

    clearPendingHover();
    pendingWrapper = wrapper;
    hoverTimer = window.setTimeout(() => {
      hoverTimer = null;
      pendingWrapper = null;
      startHoverVideo(wrapper);
    }, HOVER_DELAY);
  };

  const handlePointerOut = (event) => {
    const wrapper = event.target.closest(HOVER_VIDEO_SELECTOR);
    if (!wrapper || wrapper.contains(event.relatedTarget)) return;

    if (isAutoplayWrapper(wrapper)) {
      hideAutoplayImage(wrapper);
      return;
    }

    if (pendingWrapper === wrapper) {
      clearPendingHover();
    }

    stopHoverVideo(wrapper);
  };

  const stopActiveHoverVideo = () => {
    clearPendingHover();
    stopHoverVideo(activeWrapper);
  };

  const observeAutoplayWrappers = (root) => {
    if (!autoplayObserver) return;

    if (root.nodeType === Node.ELEMENT_NODE && root.matches(AUTOPLAY_VIDEO_SELECTOR)) {
      autoplayObserver.observe(root);
    }

    if (typeof root.querySelectorAll === "function") {
      root.querySelectorAll(AUTOPLAY_VIDEO_SELECTOR).forEach((wrapper) => {
        autoplayObserver.observe(wrapper);
      });
    }
  };

  const forgetHoverVideoWrappers = (root) => {
    const wrappers = [];

    if (root.nodeType === Node.ELEMENT_NODE && root.matches(HOVER_VIDEO_SELECTOR)) {
      wrappers.push(root);
    }

    if (typeof root.querySelectorAll === "function") {
      wrappers.push(...root.querySelectorAll(HOVER_VIDEO_SELECTOR));
    }

    wrappers.forEach((wrapper) => {
      if (wrapper.isConnected && wrapper.ownerDocument === document) return;

      if (pendingWrapper === wrapper) {
        clearPendingHover();
      }

      if (autoplayObserver && isAutoplayWrapper(wrapper)) {
        autoplayObserver.unobserve(wrapper);
      }

      visibleAutoplayWrappers.delete(wrapper);
      stopHoverVideo(wrapper);
    });
  };

  const stopAutoplayVideos = (clearVisibleWrappers) => {
    visibleAutoplayWrappers.forEach((wrapper) => {
      stopHoverVideo(wrapper);
    });

    if (clearVisibleWrappers) {
      visibleAutoplayWrappers.clear();
    }
  };

  const setupAutoplayObserver = () => {
    if (!hoverVideoMedia.matches || autoplayObserver) return;

    autoplayObserver = new IntersectionObserver((entries, observer) => {
      if (observer !== autoplayObserver || !hoverVideoMedia.matches) return;

      entries.forEach((entry) => {
        const wrapper = entry.target;

        if (!wrapper.isConnected || wrapper.ownerDocument !== document) {
          observer.unobserve(wrapper);
          visibleAutoplayWrappers.delete(wrapper);
          stopHoverVideo(wrapper);
          return;
        }

        if (entry.isIntersecting) {
          visibleAutoplayWrappers.add(wrapper);

          if (wrapper.matches(":hover")) {
            showAutoplayImage(wrapper);
          } else {
            playHoverVideo(wrapper);
          }

          return;
        }

        visibleAutoplayWrappers.delete(wrapper);
        stopHoverVideo(wrapper);
      });
    }, { threshold: 0.01 });

    observeAutoplayWrappers(document);
  };

  const teardownAutoplayObserver = () => {
    if (autoplayObserver) {
      autoplayObserver.disconnect();
      autoplayObserver = null;
    }

    stopAutoplayVideos(true);
  };

  const resumeAutoplayVideos = () => {
    if (!hoverVideoMedia.matches || document.hidden) return;

    visibleAutoplayWrappers.forEach((wrapper) => {
      if (wrapper.isConnected) {
        if (wrapper.matches(":hover")) {
          showAutoplayImage(wrapper);
        } else {
          playHoverVideo(wrapper);
        }
      } else {
        visibleAutoplayWrappers.delete(wrapper);
      }
    });
  };

  const autoplayMutationObserver = new MutationObserver((mutations) => {
    const removedRoots = [];

    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach(observeAutoplayWrappers);
      mutation.removedNodes.forEach((node) => {
        removedRoots.push(node);
      });
    });

    removedRoots.forEach(forgetHoverVideoWrappers);
  });

  document.addEventListener("pointerover", handlePointerOver);
  document.addEventListener("pointerout", handlePointerOut);
  document.addEventListener("theme:loading:start", () => {
    stopActiveHoverVideo();
    stopAutoplayVideos(false);
  });
  document.addEventListener("theme:loading:end", () => {
    window.requestAnimationFrame(() => {
      observeAutoplayWrappers(document);
      resumeAutoplayVideos();
    });
  });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      stopActiveHoverVideo();
      stopAutoplayVideos(false);
      return;
    }

    resumeAutoplayVideos();
  });
  window.addEventListener("blur", () => {
    stopActiveHoverVideo();
    stopAutoplayVideos(false);
  });
  window.addEventListener("focus", resumeAutoplayVideos);

  autoplayMutationObserver.observe(document.body, { childList: true, subtree: true });
  setupAutoplayObserver();

  if (typeof hoverVideoMedia.addEventListener === "function") {
    hoverVideoMedia.addEventListener("change", (event) => {
      stopActiveHoverVideo();

      if (event.matches) {
        setupAutoplayObserver();
      } else {
        teardownAutoplayObserver();
      }
    });
  } else if (typeof hoverVideoMedia.addListener === "function") {
    hoverVideoMedia.addListener((event) => {
      stopActiveHoverVideo();

      if (event.matches) {
        setupAutoplayObserver();
      } else {
        teardownAutoplayObserver();
      }
    });
  }
})();
